import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { blogApi } from '../api/cms';
import { defaultArticles } from '../api/defaults';
import type { BlogArticle } from '../types';

export default function BlogPage() {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    blogApi.list()
      .then(setArticles)
      .catch((_e: unknown) => setArticles(defaultArticles))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id: string) => {
    if (!confirm('Ștergeți articolul?')) return;
    await blogApi.delete(id).catch((_e: unknown): null => null);
    setArticles(prev => prev.filter(a => a.id !== id));
  };

  const filtered = articles.filter(a =>
    a.title.toLowerCase().includes(filter.toLowerCase()) ||
    a.category.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Blog</h1>
          <p className="text-white/40 text-sm mt-1">{articles.length} articole</p>
        </div>
        <Link to="/blog/new"
          className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={16} /> Articol nou
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input value={filter} onChange={e => setFilter(e.target.value)}
          className="w-full max-w-sm bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors"
          placeholder="Caută articole..." />
      </div>

      {/* Table */}
      <div className="bg-[#07090f] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-white/30 text-sm">Se încarcă...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-white/30 text-sm">Niciun articol găsit.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3 text-xs text-white/40 font-medium uppercase tracking-wide">Titlu</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide hidden md:table-cell">Categorie</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide hidden sm:table-cell">Data</th>
                <th className="text-left px-4 py-3 text-xs text-white/40 font-medium uppercase tracking-wide">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map(a => (
                <tr key={a.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="text-sm text-white font-medium truncate max-w-xs">{a.title}</div>
                    <div className="text-xs text-white/30 mt-0.5 truncate">{a.excerpt.slice(0, 60)}...</div>
                  </td>
                  <td className="px-4 py-3.5 hidden md:table-cell">
                    <span className="text-xs text-white/50 bg-white/5 px-2 py-1 rounded">{a.category}</span>
                  </td>
                  <td className="px-4 py-3.5 text-xs text-white/40 hidden sm:table-cell">{a.createdAt}</td>
                  <td className="px-4 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {a.status === 'published' ? 'Publicat' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2 justify-end">
                      <Link to={`/blog/${a.id}`}
                        className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all">
                        <Pencil size={14} />
                      </Link>
                      <button onClick={() => del(a.id)}
                        className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
